import debug from 'Helper/debugHelpers'
import moment from 'moment-timezone'
/**
 * Calculates the time difference between the timestamp provided and now
 * now() - timestamp
 * @param  {[string]} timestamp A unix time stamp in the format:
 *                              yyyy-mm-dd hh:mm:ss
 * @param  {[string]} timezone  A timezone string
 * @return {[string]}           A unix time stamp in the format:
 *                              yyyy-mm-dd hh:mm:ss
 */
export function waitTime(timestamp, timezone) {

  let date, now

  if (typeof timezone === 'string') {
    date = moment.tz(timestamp, timezone)
    now = moment().tz(timezone)
  } else {
    date = moment(timestamp)
    now = moment()
  }
  return formatTime(
    moment
      .utc(moment.duration(now.diff(date))
        .asMilliseconds()
      )
  )
}

/**
 * Calculates the time difference between the timestamp provided and now
 * timestamp - now()
 * @param  {[string]} timestamp A unix time stamp in the format:
 *                              yyyy-mm-dd hh:mm:ss
 * @param  {[string]} timezone  A timezone string
 * @return {[string]}           A unix time stamp in the format:
 *                              yyyy-mm-dd hh:mm:ss
 */
export function remainingTime(timestamp, timezone) {

  const date = moment.tz(timestamp, timezone)
  const now = moment().tz(timezone)

  return formatTime(
    moment
      .utc(moment.duration(date.diff(now))
        .asMilliseconds()
      )
  )
}

/**
 * accepts a moment duration and formats it to HH:MM:SS string if valid
 */
function formatTime(duration) {
  if (duration <= 0 || isNaN(duration)) {
    return '00:00:00'
  }

  return duration.format('HH:mm:ss')
}

export function buildWaitLists(patients, lists = {}) {
  // pull the individual lists from the current lists object if present
  const withInjectionStaffWindow = [...(lists.withInjectionStaffWindow || [])]
  const waitingInjectionWindow = [...(lists.waitingInjectionWindow || [])]
  const waitingExcuseWindow = [...(lists.waitingExcuseWindow || [])]
  const userTimezone = lists.userTimezone

  lists = { withInjectionStaffWindow, waitingInjectionWindow, waitingExcuseWindow }
  // build the waiting patient
  debug.groupStart('Building wait lists', debug.logLevels.WAITLIST)
  patients = Array.isArray(patients) ? patients : [patients]
  patients.forEach(patient => {
    let waitingPatient = {
      clinic_id: patient.clinic_id,
      tray_location: patient.tray_location,
      excuse_time: patient.excuse_time,
      last_departure_attempt: patient.last_departure_attempt,
      scheduled_departure: patient.scheduled_departure,
      login_notes: patient.login_notes,
      patient_id: patient.patient_id,
      login_time: patient.login_time,
      waitlist_id: patient.waitlist_id,
      name: patient.name,
      manual_login: patient.manual_login === true,
      time: '00:00:00',
      null_1: null, // these are temp hacks to fix column order bug
      null_2: null, // in the react-redux-grid package
      null_3: null,
      null_4: null,
      // need to update this to point at the lock stream or update
      // the waitlist stream to resent the patient when the lock changes
      locked: patient.locks != null && patient.locks.length > 0,
      // who has claimed this patient (null when unclaimed). shape: { user_id, displayname }
      claimed_by_user: patient.claimed_by_user || null,
      state: patient.state
    }
    debug.logObject('built patient:', waitingPatient, debug.logLevels.WAITLIST)
    waitingPatient = applyTimeToPatient(waitingPatient, userTimezone)
    lists = removePatientFromLists(waitingPatient, lists)
    lists = addPatientToList(waitingPatient, lists)
  })
  debug.groupEnd()

  return {
    withInjectionStaffWindow: lists.withInjectionStaffWindow,
    waitingInjectionWindow: lists.waitingInjectionWindow,
    waitingExcuseWindow: lists.waitingExcuseWindow
  }
}


export const applyTimeToPatient = (Patient, timezone) => {
  let time;
  switch (Patient.state) {
    case 'waiting_for_injection':
      time = waitTime(Patient.login_time, timezone)
      break
    case 'waiting_to_be_excused':
      time = remainingTime(Patient.scheduled_departure, timezone)
      break
    default:
      time = '00:00:00'
  }
  return {
    ...Patient,
    time
  }
}

export const loginSort = (a, b) => moment(a.login_time).isAfter(moment(b.login_time)) ? 1 : -1
export const excuseSort = (a, b) => moment(a.excuse_time).isAfter(moment(b.excuse_time)) ? 1 : -1

export function getNumPages(List, PAGE_SIZE) {
  if (List.length > PAGE_SIZE) {
    // need to subtract 1 to make the number of pages 0 based
    return Math.ceil(List.length / PAGE_SIZE) - 1
  } else {
    return 0
  }
}

export const removePatientFromLists = (encounter, { waitingExcuseWindow, waitingInjectionWindow, withInjectionStaffWindow }) => {
  // encounters and waitlists are the same root object so if the encounter is actually a "waitlist" we need to change th waitlist_id to encounter_id
  if(!encounter.waitlist_id) {
    encounter.waitlist_id = encounter.encounter_id
  }

  const patientFilter = testPatient => (testPatient.patient_id !== encounter.patient_id || testPatient.manual_login === true) && testPatient.waitlist_id !== encounter.waitlist_id
  waitingExcuseWindow = waitingExcuseWindow.filter(patientFilter)
  withInjectionStaffWindow = withInjectionStaffWindow.filter(patientFilter)
  waitingInjectionWindow = waitingInjectionWindow.filter(patientFilter)
  return {
    waitingExcuseWindow,
    withInjectionStaffWindow,
    waitingInjectionWindow
  }
}

export const addPatientToList = (encounter, { waitingExcuseWindow, waitingInjectionWindow, withInjectionStaffWindow }) => {
  // encounters and waitlists are the same root object so if the encounter is actually a "waitlist" we need to change th waitlist_id to encounter_id
  if(!encounter.waitlist_id) {
    encounter.waitlist_id = encounter.encounter_id
  }
  switch (encounter.state) {
    case 'waiting_for_injection':
      waitingInjectionWindow.push({
        ...encounter
      })
      break
    case 'with_injection_staff':
      withInjectionStaffWindow.push({
        ...encounter
      })
      break
    case 'waiting_to_be_excused':
      waitingExcuseWindow.push({
        ...encounter
      })
      break
  }
  return {
    waitingExcuseWindow,
    withInjectionStaffWindow,
    waitingInjectionWindow
  }
}
